import type {
  i18n as I18nInstance,
  Module,
  Newable,
  NewableModule,
} from "i18next";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

interface I18nInitOptions {
  /** i18next plugins (e.g. LanguageDetector) to register before init. */
  plugins?: Array<Module | NewableModule<Module> | Newable<Module>>;
  /** Enable automatic language detection. */
  languageDetection?: {
    supportedLanguages: readonly string[];
    order?: string[];
    lookupLocalStorage?: string;
  };
  setup?: (i18n: I18nInstance) => void;
  missingKeyHandler?: (
    lngs: readonly string[],
    ns: string,
    key: string
  ) => void;
}

/**
 * Creates a minimal i18n setup with German as default language.
 * Optionally registers plugins (e.g. LanguageDetector) and configures
 * language detection.
 */
export function createI18nInit(options: I18nInitOptions = {}) {
  let instance = i18n;
  for (const plugin of options.plugins ?? []) {
    instance = instance.use(plugin);
  }

  const i18nInitPromise = instance.use(initReactI18next).init({
    lng: options.languageDetection ? undefined : "de",
    fallbackLng: "de",
    interpolation: { escapeValue: false },
    ...(options.languageDetection
      ? {
          supportedLngs: [...options.languageDetection.supportedLanguages],
          detection: {
            order: options.languageDetection.order ?? [
              "localStorage",
              "navigator",
            ],
            lookupLocalStorage:
              options.languageDetection.lookupLocalStorage ?? "i18nextLng",
            caches: ["localStorage"],
          },
        }
      : {}),
    ...(options.missingKeyHandler
      ? {
          saveMissing: true,
          missingKeyHandler: options.missingKeyHandler,
        }
      : {}),
  });

  options.setup?.(i18n);

  return { i18n, i18nInitPromise };
}
